import { NavLink, Outlet, useNavigate } from "react-router";
import {
  Activity,
  Bell,
  Bot,
  Building2,
  CreditCard,
  Database,
  Globe,
  LayoutDashboard,
  LogOut,
  Mail,
  Megaphone,
  Package,
  ScrollText,
  Settings,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";

import { apiRequest, isApiError } from "@profitlens/api-client";
import type { MeOut } from "@profitlens/api-client";
import { Button } from "@profitlens/ui/button";
import { cn } from "@profitlens/ui/cn";
import { Separator } from "@profitlens/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@profitlens/ui/tooltip";

import { LanguageSwitcher } from "@/components/language-switcher";
import { setAccessToken } from "@/lib/session";

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "nav.groupMain",
    items: [
      { to: "/", labelKey: "nav.overview", icon: LayoutDashboard, end: true },
      { to: "/tenants", labelKey: "nav.tenants", icon: Building2 },
      { to: "/data", labelKey: "nav.data", icon: Database },
      { to: "/payments", labelKey: "nav.payments", icon: CreditCard },
    ],
  },
  {
    labelKey: "nav.groupConfig",
    items: [
      { to: "/settings/site", labelKey: "nav.site", icon: Settings },
      { to: "/settings/plans", labelKey: "nav.plans", icon: Package },
      { to: "/settings/channels", labelKey: "nav.channels", icon: Wallet },
      { to: "/settings/ai", labelKey: "nav.ai", icon: Bot },
      { to: "/settings/templates", labelKey: "nav.templates", icon: Mail },
      { to: "/website", labelKey: "nav.website", icon: Globe },
      { to: "/ai/usage", labelKey: "nav.aiUsage", icon: Activity },
    ],
  },
  {
    labelKey: "nav.groupOps",
    items: [
      { to: "/ops/announcements", labelKey: "nav.announcements", icon: Megaphone },
      { to: "/ops/alerts", labelKey: "nav.alerts", icon: Bell },
      { to: "/ops/audit", labelKey: "nav.audit", icon: ScrollText },
      { to: "/ops/admins", labelKey: "nav.admins", icon: ShieldCheck },
    ],
  },
];

export function AppShell() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const me = useQuery<MeOut | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await apiRequest<MeOut>("/auth/me");
      } catch (err) {
        if (isApiError(err) && err.isUnauthorized) setAccessToken(null);
        return null;
      }
    },
    staleTime: 60_000,
  });

  const logout = useMutation({
    mutationFn: async () => {
      try {
        await apiRequest("/auth/logout", { method: "POST", body: { refresh_token: null } });
      } catch {
        // best-effort：本地总会清掉 token
      }
    },
    onSettled: () => {
      setAccessToken(null);
      navigate("/login", { replace: true });
    },
  });

  return (
    <div className="flex min-h-dvh">
      {/* 左侧固定分组导航 */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
            PL
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{t("app.name")}</p>
            <p className="truncate text-xs text-muted">{t("app.tagline")}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto p-3 scrollbar-thin">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.labelKey}>
              {gi > 0 ? <Separator className="mb-3" /> : null}
              <p className="mb-1 px-3 text-[11px] font-medium tracking-wider text-muted uppercase">
                {t(group.labelKey)}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-brand-soft font-medium text-brand"
                          : "text-muted hover:bg-surface-muted hover:text-foreground",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* 右侧：顶栏 + 内容 */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 border-b border-border bg-surface/80 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2 md:hidden">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand text-xs font-bold text-brand-fg">
              PL
            </div>
            <span className="text-sm font-semibold">{t("app.name")}</span>
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            {me.data?.user?.email ? (
              <span className="hidden text-xs text-muted lg:inline">{me.data.user.email}</span>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  aria-label={t("common.logout")}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("common.logout")}</TooltipContent>
            </Tooltip>
          </div>
        </header>

        {/* 移动端横向导航（md 以下替代侧栏） */}
        <nav className="flex gap-1 overflow-x-auto border-b border-border bg-surface px-3 py-2 md:hidden scrollbar-thin">
          {NAV_GROUPS.flatMap((g) => g.items).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs whitespace-nowrap transition-colors",
                  isActive
                    ? "bg-brand-soft font-medium text-brand"
                    : "text-muted hover:bg-surface-muted hover:text-foreground",
                )
              }
            >
              <item.icon className="h-3.5 w-3.5" />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 md:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
